import { useCallback, useEffect, useState } from "react";
import {
  launcherRegister,
  getlLauncherUser,
  loginUser,
  loginUserWithCredentials,
  getAppSettings,
} from "@server/requests.js";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectConfigs, selectAllSettings } from "@app/configs/configsSlice.js";

import LOCAL_STORAGE from "@utils/localStorage.js";
import PATHS from "@utils/paths";

import useKeydown from "@hooks/useKeydown";

import Loading from "@components/common/Loading.jsx";
import Button from "@components/common/Button";
import VoucherModal from "@components/modals/VoucherModal";
import LoginModal from "@components/modals/LoginModal";

import "@styles/components/activationPage.scss";

export default function ActivationPage() {
  const navigate = useNavigate();

  const configs = useSelector(selectConfigs);
  const allSettings = useSelector(selectAllSettings);

  const [isLoading, setIsLoading] = useState(true);
  const [activeButton, setActiveButton] = useState(0);
  const [activationPageState, setActivationPageState] = useState();
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLauncherMode, setIsLauncherMode] = useState(true);

  // Check launcher setting from app_settings
  const checkLauncherMode = async () => {
    try {
      const lang = JSON.parse(localStorage.getItem("language"));
      if (lang) {
        const langId = lang.id;
        const appSettings = await getAppSettings({
          languageId: langId,
        });
        const parsedSettings = JSON.parse(appSettings);
        const { message } = parsedSettings;

        // Check if launcher is false in app_settings
        const isLauncher = message?.app_settings?.launcher !== false;
        setIsLauncherMode(isLauncher);
      }
    } catch (err) {
      console.log("Error checking launcher mode:", err);
      // Default to launcher mode if there's an error
      setIsLauncherMode(true);
    }
  };

  const checkDeviceMacAddress = async () => {
    const macAddress = LOCAL_STORAGE.MAC_ADDRESS.GET();

    setIsLoading(true);

    if (macAddress) {
      const user = await getlLauncherUser({
        device_id: macAddress,
      });
      const parsedUser = JSON.parse(user);
      const { message, error } = parsedUser;

      if (error) {
        setActivationPageState(message.action);
        if (message.action === "not_found") {
          registerLauncher();
        } else {
          setIsLoading(false);
        }
      } else {
        // need to login user
        localStorage.setItem(
          "userLangId",
          JSON.stringify(message.user_info?.languageId)
        );
        loginUserReq();
      }
    }
  };

  const registerLauncher = async () => {
    const register = await launcherRegister({
      device_model: LOCAL_STORAGE.DEVICE_NAME.GET(),
      device_id: LOCAL_STORAGE.MAC_ADDRESS.GET(),
      device_type: LOCAL_STORAGE.DEVICE_MODEL.GET(),
      serial_number: LOCAL_STORAGE.DEVICE_MODEL.GET(),
    });
    const parsedRegister = JSON.parse(register);
    const { message, error } = parsedRegister;

    if (!error) checkDeviceMacAddress();
  };

  const loginUserReq = async () => {
    const token = await loginUser({
      device_id: LOCAL_STORAGE.MAC_ADDRESS.GET(),
    });

    const parsedToken = JSON.parse(token);
    const { message, error } = parsedToken;
    if (!error) {
      LOCAL_STORAGE.TOKEN.SET(message);
      if (LOCAL_STORAGE.LAST_CHANNEL_ID.GET()) {
        navigate(PATHS.LIVE);
      } else {
        navigate(PATHS.MENU);
      }
    }
  };

  const handleCredentialsLogin = () => {
    if (LOCAL_STORAGE.LAST_CHANNEL_ID.GET()) {
      navigate(PATHS.LIVE);
    } else {
      navigate(PATHS.MENU);
    }
  };

  const checkAgain = useCallback(() => {
    checkDeviceMacAddress();
  }, []);

  const handleVoucherSubmit = async (code) => {
    // TODO: Implement voucher code validation
    setShowVoucherModal(false);
  };

  useEffect(() => {
    checkLauncherMode();
  }, []);

  useEffect(() => {
    if (isLauncherMode) {
      checkDeviceMacAddress();
    } else {
      // If launcher is false, show login options immediately
      setIsLoading(false);
    }
  }, [isLauncherMode]);

  useKeydown({
    isActive: !isLoading && !showVoucherModal && !showLoginModal,
    down: () => {
      if (isLauncherMode) {
        setActiveButton(activeButton === 1 ? 0 : 1);
      } else {
        setActiveButton(activeButton === 0 ? 1 : 0);
      }
    },
    up: () => {
      if (isLauncherMode) {
        setActiveButton(activeButton === 0 ? 1 : 0);
      } else {
        setActiveButton(activeButton === 1 ? 0 : 1);
      }
    },
    ok: () => {
      if (isLauncherMode) {
        if (activeButton === 0) {
          checkAgain();
        } else {
          setShowVoucherModal(true);
        }
      } else {
        if (activeButton === 0) {
          setShowLoginModal(true);
        } else {
          setShowVoucherModal(true);
        }
      }
    },
  });

  return isLoading ? (
    <div className="loading-parent">
      <Loading />
    </div>
  ) : (
    <div className="activation-page-container">
      <div className="logo">
        <img
          src={configs?.basics?.logo || LOCAL_STORAGE.LOGO.GET()}
          alt="logo"
        />
      </div>

      {isLauncherMode ? (
        <>
          <h3 className="title">DEVICE IS NOT ACTIVE</h3>
          <p className="device_id">MAC : {LOCAL_STORAGE.MAC_ADDRESS.GET()}</p>
          <Button
            index={0}
            onClick={() => {
              if (!showVoucherModal) {
                checkAgain();
              }
            }}
            onMouseEnter={() => {}}
            title={"Check Again"}
            isActive={activeButton === 0}
          />

          <div className="or-seperator">
            <span>OR</span>
          </div>

          <Button
            index={1}
            onClick={() => {
              if (!showVoucherModal) {
                setShowVoucherModal(true);
              }
            }}
            onMouseEnter={() => {}}
            title={"Activate with Code"}
            className="voucher-code-button"
            isActive={activeButton === 1}
          />
        </>
      ) : (
        <>
          <h3 className="title">LOGIN TO CONTINUE</h3>
          <p className="device_id">Choose your login method</p>

          <Button
            index={0}
            onClick={() => {
              if (!showLoginModal) {
                setShowLoginModal(true);
              }
            }}
            onMouseEnter={() => {}}
            title={"Login"}
            isActive={activeButton === 0}
          />

          <div className="or-seperator">
            <span>OR</span>
          </div>

          <Button
            index={1}
            onClick={() => {
              if (!showVoucherModal) {
                setShowVoucherModal(true);
              }
            }}
            onMouseEnter={() => {}}
            title={"Activate with Code"}
            className="voucher-code-button"
            isActive={activeButton === 1}
          />
        </>
      )}

      <VoucherModal
        isVisible={showVoucherModal}
        onClose={(doCheckAgain = false) => {
          if (doCheckAgain) {
            if (isLauncherMode) {
              checkAgain();
            } else {
              handleCredentialsLogin();
            }
          }
          setShowVoucherModal(false);
        }}
        onSubmit={handleVoucherSubmit}
      />

      <LoginModal
        isVisible={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
        }}
        onSubmit={handleCredentialsLogin}
      />
    </div>
  );
}
